import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { useApiClient } from '@/api/useApiClient'
import type { ServiceType } from '@/constants/environments'
import { useToast } from '@/composables/useToast'

/*
 * Once `npm run generate:api-all` has been run:
 * - Replace `any` with the concrete school type from api-types.ts
 */

export function useSchools(service: ServiceType) {
  const { GET } = useApiClient(service)

  return useQuery({
    queryKey: ['schools', service],
    queryFn: async () => {
      const { data, error } = await GET('/admin/schools')
      if (error) {
        throw new Error(typeof error === 'string' ? error : 'Failed to fetch schools')
      }
      // A 200 with an empty body (no schools) yields undefined — Vue Query treats
      // undefined as an error ("data is undefined"), so normalize to [].
      return (data ?? []) as any[]
    },
  })
}

export function useCreateSchool(service: ServiceType) {
  const { POST } = useApiClient(service)
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: async (body: Record<string, any>) => {
      const { data, error } = await POST('/admin/schools', { body })
      if (error) {
        throw new Error(typeof error === 'string' ? error : 'Failed to create school')
      }
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schools', service] })
      toast.success('School created successfully')
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to create school')
    },
  })
}

export function useCopySchool(service: ServiceType) {
  const { POST } = useApiClient(service)
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: async (body: Record<string, any>) => {
      const { data, error } = await POST('/admin/school-copy', { body })
      if (error) {
        throw new Error(typeof error === 'string' ? error : 'Failed to copy school')
      }
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schools', service] })
      toast.success('School copied successfully')
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to copy school')
    },
  })
}
